import { useMutation, useQueryClient } from '@tanstack/react-query';
import { n8nFetch } from '../n8nClient';
import type { Empresa } from '../types';

interface EmpresaUpdateInput {
  id: string;
  payload: Partial<Empresa> & { redes?: Record<string, string> };
}

export function useEmpresaUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: EmpresaUpdateInput) => {
      return n8nFetch<{ id: string; nome: string }>('/empresa-update', {
        method: 'POST',
        body: JSON.stringify({ id, payload }),
      });
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['empresa', vars.id] });
      qc.invalidateQueries({ queryKey: ['empresas'] });
      qc.invalidateQueries({ queryKey: ['pipeline'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

interface EnrichUrlResponse {
  ok: boolean;
  empresa_id: string;
  nome: string;
  site?: string;
  redes?: Record<string, string>;
  found?: { url: string; redes: Record<string, string>; title?: string | null };
}

export function useEnrichByUrl() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, url }: { id: string; url: string }) => {
      return n8nFetch<EnrichUrlResponse>('/empresa-enrich-url', {
        method: 'POST',
        body: JSON.stringify({ id, url }),
      });
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['empresa', vars.id] });
      qc.invalidateQueries({ queryKey: ['empresas'] });
    },
  });
}

interface EnrichCnpjResponse {
  ok: boolean;
  empresa?: { id: string; nome: string; razao_social?: string; cnpj?: string; setor?: string };
  found?: {
    cnpj: string;
    razao_social?: string;
    setor?: string;
    endereco?: Record<string, string | null>;
    telefone?: { numero: string; tipo: string } | null;
  };
}

interface EmpresaCreateInput {
  payload: Partial<Empresa> & { nome: string };
}

export function useEmpresaCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ payload }: EmpresaCreateInput) => {
      return n8nFetch<{ id: string; nome: string; setor: string; status_funil: string }>(
        '/empresa-create',
        {
          method: 'POST',
          body: JSON.stringify({ payload }),
        },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['empresas'] });
    },
  });
}

export function useEnrichByCnpj() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, cnpj }: { id: string; cnpj: string }) => {
      return n8nFetch<EnrichCnpjResponse>('/empresa-enrich-cnpj', {
        method: 'POST',
        body: JSON.stringify({ id, cnpj }),
      });
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['empresa', vars.id] });
      qc.invalidateQueries({ queryKey: ['enderecos', vars.id] });
      qc.invalidateQueries({ queryKey: ['empresas'] });
    },
  });
}
